// Logs (APS-327; Console D `#/logs`): what the app logged, across every session. One query
// (tokens and words, lib/logsPage.ts) in the URL; the warnings and errors per hour with the
// release's ship time marked; the matching logs newest first, each opening to its fields; and
// the facets that toggle the tokens, each counted without its own filter. The API builds the SQL
// (POST /explore/logs and /explore/logs/facets) and reads as the project, sandbox included.
// Records are the app's data: shown as text, never as markup, never translated.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, Fragment, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  fetchExploreLogFacets,
  fetchExploreLogs,
  lastDays,
  projectsQuery,
  releasesQuery,
} from '../api/queries'
import type { ExploreLogFacets, ExploreLogRecord } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import {
  Btn,
  copyText,
  EmptyNote,
  HovRow,
  PageHead,
  Section,
  SectionHead,
  Table,
  TableFoot,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { useWidth } from '../components/LineChart'
import { deviceMeta } from '../components/Perf'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { ATTR } from '../lib/conventions'
import { shortId } from '../lib/format'
import {
  agentContext,
  type LogToken,
  logsQuery,
  logTokens,
  parseLogWords,
  TOKEN_KEY,
  tailCommand,
  type WordsError,
} from '../lib/logsPage'
import { releasesNewestFirst } from '../lib/releases'
import { runError } from './exploreQuery'
import { useProjectParams } from './params'
import { compactCount } from './Sessions'
import {
  carried,
  clampRange,
  deviceParams,
  LOG_LEVELS,
  type LogLevel,
  type LogsSearch,
  RANGE_DAYS,
} from './search'

const HOUR = 3_600_000
const PAGE = 50
/** The facets, in the mock's order; logger and screen show only once some record has one. */
const FACETS = ['level', 'logger', 'screen', 'release', 'os'] as const
type Facet = (typeof FACETS)[number]
const FACET_TOKEN: Record<Facet, Exclude<LogToken, 'q'>> = {
  level: 'level',
  logger: 'logger',
  screen: 'screen',
  release: 'release',
  os: 'os',
}

/** A record's level as the mock writes it, from its severity number (OTLP ranges). */
export function levelOf(n: number): LogLevel | 'trace' {
  if (n >= 21) return 'fatal'
  if (n >= 17) return 'error'
  if (n >= 13) return 'warn'
  if (n >= 9) return 'info'
  if (n >= 5) return 'debug'
  return 'trace'
}

const rank = (level: string) => LOG_LEVELS.indexOf(level as LogLevel)

function wordsErrorText(t: Translator['t'], e: WordsError): string {
  switch (e.code) {
    case 'level':
      return t('logs.q.err.level', { value: e.value, levels: LOG_LEVELS.join(', ') })
    case 'os':
      return t('logs.q.err.os', { value: e.value })
    case 'quote':
      return t('logs.q.err.quote')
    default:
      return t('logs.q.err.tooLong')
  }
}

export function LogsPage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as LogsSearch
  const navigate = useNavigate()
  const { toast, show } = useToast()
  const range = clampRange(search.range, 'logs')
  const days = RANGE_DAYS[range]
  // A minute's floor, so the three reads share one window and the shell's keys (lib/navCounts).
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const hours = Math.round(days * 24)
  const end = Math.floor(now / HOUR) * HOUR + HOUR
  const span = { from: new Date(end - hours * HOUR).toISOString(), to: new Date(end).toISOString() }

  const query = logsQuery(search, span)
  const records = useInfiniteQuery({
    queryKey: ['project', projectId, 'logs', query],
    queryFn: ({ pageParam }) => fetchExploreLogs(projectId, { ...query, limit: PAGE }, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
  })
  const chartQuery = logsQuery(search, span, true)
  const chart = useQuery({
    queryKey: ['project', projectId, 'logs-chart', chartQuery],
    queryFn: () => fetchExploreLogs(projectId, { ...chartQuery, limit: 1, bucket: 'hour' }),
    retry: false,
  })
  const facets = useQuery({
    queryKey: ['project', projectId, 'logs-facets', query],
    queryFn: () => fetchExploreLogFacets(projectId, query),
    retry: false,
  })
  const releases = useQuery(releasesQuery(projectId, lastDays(30, now), ['window', 30, now]))
  const projects = useQuery(projectsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const project = projects.data?.find((p) => p.id === projectId)
  const app = apps.data?.find((a) => a.id === search.app)

  // The release marked on the chart: the chip's, else the app's newest, when it shipped inside.
  const shipped = (() => {
    const list = releasesNewestFirst(releases.data ?? [], search.app)
    const r = search.release ? list.find((x) => x.release === search.release) : list[0]
    if (!r) return undefined
    const at = Date.parse(r.released_at)
    const index = Math.floor((at - (end - hours * HOUR)) / HOUR)
    return index >= 0 && index < hours ? { release: r.release, at, index } : undefined
  })()

  const set = (patch: Partial<LogsSearch>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })

  const meta = [
    project?.name,
    app ? appLabel(app) : t('shell.allAppsShort'),
    search.release ?? t('sessions.meta.allReleases'),
    days === 1 ? t('issues.chip.day') : t('issues.chip.days', { days }),
    // Logs filter by their own `os:` token: the bar's device filter is said not to apply (APS-228).
    deviceMeta(i18n, undefined, deviceParams(search), 'na'),
  ]
    .flatMap((part) => (part ? part.split(' · ') : []))
    .filter((part, i, all) => part !== all[i - 1])
    .join(' · ')

  const first = records.data?.pages[0]
  const rows = records.data?.pages.flatMap((p) => p.records) ?? []
  const total = facets.data
    ? facets.data.level
        .filter((v) => !search.level || rank(v.value) >= rank(search.level))
        .reduce((n, v) => n + v.count, 0)
    : undefined

  return (
    <main id="main" className="pg lg-pg">
      <PageHead
        title={t('nav.logs')}
        meta={<span translate="no">{meta}</span>}
        actions={
          <>
            <Btn
              onClick={async () =>
                show(
                  (await copyText(tailCommand(search)))
                    ? t('logs.copiedTail')
                    : t('issues.copyFailed'),
                )
              }
            >
              <Icon name="explore" />
              {t('logs.copyTail')}
            </Btn>
            <Btn
              onClick={async () =>
                show(
                  (await copyText(
                    agentContext(search, span, { id: projectId, name: project?.name }),
                  ))
                    ? t('issues.copied')
                    : t('issues.copyFailed'),
                )
              }
            >
              <Icon name="agent" />
              {t('issues.copyForAgent')}
            </Btn>
          </>
        }
      />
      <QueryField
        search={search}
        osName={app?.platform === 'android' ? 'Android' : app ? 'iOS' : undefined}
        onChange={set}
      />

      <Section title={t('logs.chart.title')} meta={t('logs.chart.meta')}>
        {chart.isPending ? (
          <Loading />
        ) : chart.isError ? (
          <RunError error={chart.error} retry={() => chart.refetch()} />
        ) : (
          <PerHour
            histogram={chart.data.histogram}
            start={end - hours * HOUR}
            hours={hours}
            shipped={shipped}
          />
        )}
      </Section>

      <div className="lg-grid">
        <section className="sec lg-res" aria-labelledby="lg-res-h">
          <SectionHead
            id="lg-res-h"
            title={t('logs.records.title')}
            meta={
              total !== undefined && first
                ? t('logs.records.count', {
                    total: compactCount(i18n, total),
                    shown: rows.length,
                  })
                : undefined
            }
          />
          {records.isPending ? (
            <Loading />
          ) : records.isError ? (
            <RunError error={records.error} retry={() => records.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyNote title={t('logs.none')}>
              {t('logs.noneHint')}
              <DebugOnlyLine projectId={projectId} scope={{ app: search.app, days }} empty />
            </EmptyNote>
          ) : (
            <>
              <Records rows={rows} search={search} />
              {records.hasNextPage ? (
                <TableFoot end>
                  <Btn
                    size="sm"
                    disabled={records.isFetchingNextPage}
                    onClick={() => records.fetchNextPage()}
                  >
                    {t('logs.more', { count: PAGE })}
                  </Btn>
                </TableFoot>
              ) : null}
            </>
          )}
        </section>
        <aside className="sec" aria-labelledby="lg-fac-h">
          <SectionHead id="lg-fac-h" title={t('logs.facets.title')} />
          {facets.isPending ? (
            <Loading />
          ) : facets.isError ? (
            <RunError error={facets.error} retry={() => facets.refetch()} />
          ) : (
            <Facets data={facets.data} search={search} onChange={set} />
          )}
        </aside>
      </div>
      {toast}
    </main>
  )
}

/** A run's failure: Explore's limits in its words, anything else as every page says it. */
function RunError({ error, retry }: { error: unknown; retry: () => void }) {
  const { t } = useI18n()
  return [400, 422, 429, 503].some((status) => isApiError(error, status)) ? (
    <p role="alert" className="notice error">
      {runError(t, error)}
    </p>
  ) : (
    <ErrorState error={error} retry={retry} />
  )
}

/** The tokens and the words being typed; Run turns the words into tokens. */
function QueryField({
  search,
  osName,
  onChange,
}: {
  search: LogsSearch
  osName: string | undefined
  onChange: (patch: Partial<LogsSearch>) => void
}) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [error, setError] = useState<WordsError | null>(null)
  const ids = { input: useId(), hint: useId(), error: useId() }
  const tokens = logTokens(search)
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new query clears the error
  useEffect(() => setError(null), [JSON.stringify(search)])

  function submit(e: FormEvent) {
    e.preventDefault()
    const parsed = parseLogWords(text, search, osName)
    if ('error' in parsed) return setError(parsed.error)
    setError(null)
    setText('')
    onChange(parsed.patch)
  }
  const tokenText = (key: LogToken, value: string) => (key === 'q' ? `"${value}"` : value)

  return (
    <form className="lg-qs" onSubmit={submit} aria-label={t('logs.q.form')}>
      <div className="lg-qrow">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: a click on the field's padding focuses its input */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: the input itself takes the keys */}
        <div
          className="lg-qf"
          onClick={(e) => {
            if (!(e.target as HTMLElement).closest('button'))
              document.getElementById(ids.input)?.focus()
          }}
        >
          <Icon name="search" />
          {tokens.map((tok) => (
            <span key={tok.key} className="lg-tok" translate="no">
              {tok.key === 'q' ? null : <span className="lg-tk">{tok.key}:</span>}
              {tokenText(tok.key, tok.value)}
              <button
                type="button"
                aria-label={t('logs.q.remove', { key: tok.key, value: tok.value })}
                onClick={() => onChange({ [TOKEN_KEY[tok.key]]: undefined })}
              >
                <Icon name="x" small />
              </button>
            </span>
          ))}
          <input
            id={ids.input}
            className="lg-in"
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            translate="no"
            maxLength={600}
            placeholder={t('logs.q.placeholder')}
            aria-label={t('logs.q.label')}
            aria-describedby={error ? ids.error : ids.hint}
            aria-invalid={error ? true : undefined}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              const last = tokens.at(-1)
              if (e.key === 'Backspace' && !text && last)
                onChange({ [TOKEN_KEY[last.key]]: undefined })
            }}
          />
        </div>
        <Btn type="submit" variant="solid">
          {t('explore.run')}
        </Btn>
      </div>
      <div className="lg-sub">
        {error ? (
          <p id={ids.error} role="alert" className="lg-err">
            {wordsErrorText(t, error)}
          </p>
        ) : (
          <span id={ids.hint} className="hints" translate="no">
            <span>
              {t('logs.q.filters')} <code>level:</code> <code>logger:</code> <code>screen:</code>{' '}
              <code>release:</code> <code>os:</code> <code>session:</code>
            </span>
            <span>{t('logs.q.levelHint', { token: 'level:error' })}</span>
          </span>
        )}
      </div>
    </form>
  )
}

/** Warnings and errors per hour, errors stacked on warnings, the release's ship time dashed. */
function PerHour({
  histogram,
  start,
  hours,
  shipped,
}: {
  histogram: { t: string; warn: number; error: number }[]
  start: number
  hours: number
  shipped: { release: string; at: number; index: number } | undefined
}) {
  const i18n = useI18n()
  const { t, date, number } = i18n
  const [ref, width] = useWidth(960)
  const err = new Array<number>(hours).fill(0)
  const warn = new Array<number>(hours).fill(0)
  for (const b of histogram) {
    const i = Math.floor((Date.parse(b.t) - start) / HOUR)
    if (i < 0 || i >= hours) continue
    err[i] = (err[i] ?? 0) + b.error
    warn[i] = (warn[i] ?? 0) + b.warn
  }
  const sum = (a: number[]) => a.reduce((x, v) => x + v, 0)
  const H = 186
  const L = 34
  const R = 8
  const T = 22
  const B = 22
  const bw = (width - L - R) / hours
  const top = nice(Math.max(1, ...err.map((v, i) => v + (warn[i] ?? 0))))
  const y = (v: number) => T + (1 - v / top) * (H - T - B)
  const g = bw > 3 ? 0.5 : 0
  // Four dates under the bars (hours on a day's range), counted back from the last hour, so
  // the last one is the window's last day (Mosaic).
  const step = Math.max(1, Math.round(hours / 4))
  const ticks = [3, 2, 1, 0].map((k) => hours - 1 - k * step).filter((i) => i >= 0)
  const tickText = (i: number) =>
    hours > 24
      ? date(new Date(start + i * HOUR).toISOString(), { month: 'short', day: 'numeric' })
      : date(new Date(start + i * HOUR).toISOString(), { hour: '2-digit', minute: '2-digit' })
  const mx = shipped ? L + shipped.index * bw : 0
  const label = t('logs.chart.label', {
    errors: number(sum(err)),
    warnings: number(sum(warn)),
    days: Math.round(hours / 24),
  })
  return (
    <>
      <div ref={ref} className="lg-ch">
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${H}`}
          width="100%"
          height={H}
          role="img"
          aria-label={label}
        >
          {[0, 1, 2].map((k) => {
            const v = (k / 2) * top
            return (
              <g key={`g${k}`}>
                <line className="grid" x1={L} x2={width - R} y1={y(v)} y2={y(v)} />
                <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
                  {number(v)}
                </text>
              </g>
            )
          })}
          <text className="ax" x={L - 6} y={9} textAnchor="end">
            {t('logs.chart.unit')}
          </text>
          {err.map((e, h) => {
            const x = L + h * bw + g
            const w = Math.max(0.6, bw - 2 * g)
            const hw = H - B - y(warn[h] ?? 0)
            const he = H - B - y(e)
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
              <g key={h}>
                <rect className="b1" x={x} y={H - B - hw} width={w} height={hw} />
                <rect className="b1 hi" x={x} y={H - B - hw - he} width={w} height={he} />
              </g>
            )
          })}
          <line className="base" x1={L} x2={width - R} y1={H - B} y2={H - B} />
          {ticks.map((i) => (
            // The last one ends at the right edge, so it is never cut.
            <text
              key={`x${i}`}
              className="ax"
              x={i === hours - 1 ? L + hours * bw : L + (i + 0.5) * bw}
              y={H - 6}
              textAnchor={i === hours - 1 ? 'end' : 'middle'}
            >
              {tickText(i)}
            </text>
          ))}
          {shipped ? (
            <g>
              {/* A halo in the page's colour under the dashes keeps them apart from a bar. */}
              <line className="mk-halo" x1={mx} x2={mx} y1={2} y2={H - B} />
              <line className="mk" x1={mx} x2={mx} y1={2} y2={H - B} />
              <text
                className="mk-t"
                {...(mx > width - 160 ? { x: mx - 5, textAnchor: 'end' } : { x: mx + 5 })}
                y={10}
              >
                {t('logs.chart.shipped', { release: shipped.release })}
              </text>
            </g>
          ) : null}
        </svg>
      </div>
      <div className="legend">
        <span>
          <i />
          {t('logs.chart.errors')}
        </span>
        <span>
          <i className="s3" />
          {t('logs.chart.warn')}
        </span>
        {shipped ? (
          <span>
            {t('logs.chart.marker', {
              release: shipped.release,
              date: date(new Date(shipped.at).toISOString(), {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </span>
        ) : null}
      </div>
    </>
  )
}

/** The top of the chart's scale: 1, 2, 4 or 5 times a power of ten. */
export function nice(max: number): number {
  const e = 10 ** Math.floor(Math.log10(Math.max(max, 1)))
  for (const s of [1, 2, 4, 5, 10]) if (s * e >= max) return s * e
  return 10 * e
}

/** A record's fields as JSON, in the mock's order: its own, then its attributes. */
export function recordFields(r: ExploreLogRecord): [string, string | number][] {
  const own: [string, string | number][] = [
    ['timestamp', r.ts],
    ['level', r.severity_text || levelOf(r.severity_number)],
    ['logger', r.logger],
    ['message', r.body],
    ['event.name', r.event_name],
    ['session.id', r.session_id],
    ['device.model.identifier', r.device_model],
    ['os.name', r.os_name],
    ['os.version', r.os_version],
    ['service.version', r.release],
    ['trace_id', r.trace_id],
  ]
  return [
    ...own.filter(([, v]) => v !== ''),
    ...Object.entries(r.attributes).sort(([a], [b]) => a.localeCompare(b)),
  ]
}

function Records({ rows, search }: { rows: ExploreLogRecord[]; search: LogsSearch }) {
  const { t, date } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const { toast, show } = useToast()
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [cur, setCur] = useState(0)
  const carry = carried(search)
  const toggle = (id: string, i: number) => {
    setCur(i)
    const next = new Set(open)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setOpen(next)
  }
  const newest = rows[0]?.ts
  const time = (ts: string) => {
    const d = new Date(ts)
    return `${date(ts, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`
  }
  return (
    <>
      <Table label={t('logs.records.table')} rowKeys>
        <thead>
          <tr>
            <th className="f" scope="col">
              {newest
                ? t('logs.col.timeOn', { date: date(newest, { month: 'short', day: 'numeric' }) })
                : t('logs.col.time')}
            </th>
            <th scope="col">{t('logs.col.level')}</th>
            <th scope="col">{t('logs.col.message')}</th>
            <th scope="col">{t('logs.col.device')}</th>
            <th className="last" scope="col">
              {t('logs.col.session')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const expanded = open.has(r.id)
            const level = levelOf(r.severity_number)
            const attrs = Object.entries(r.attributes)
              .filter(([k]) => k !== ATTR.screenName)
              .slice(0, 3)
              .map(([k, v]) => `${k}=${v}`)
              .join(' ')
            const screen = r.attributes[ATTR.screenName]
            return (
              <Fragment key={r.id}>
                <HovRow
                  cur={i === cur}
                  className="lg-r"
                  label={t(expanded ? 'logs.row.close' : 'logs.row.open')}
                  onOpen={() => toggle(r.id, i)}
                >
                  <td className="f lg-tw">
                    <button
                      type="button"
                      className="lg-car-b"
                      aria-expanded={expanded}
                      aria-label={t(expanded ? 'logs.row.close' : 'logs.row.open')}
                      onClick={() => toggle(r.id, i)}
                    >
                      <span className="lg-car" aria-hidden="true">
                        ›
                      </span>
                    </button>
                    {time(r.ts)}
                  </td>
                  <td>
                    <span className={`sev ${level}`} translate="no">
                      {level}
                    </span>
                  </td>
                  <td className="wrap">
                    <div className="lg-mw" translate="no">
                      <span className="lg-msg">{r.body || r.event_name || '–'}</span>
                      {r.logger || attrs ? (
                        <span className="t2">
                          {r.logger ? <span className="lg-lg">{r.logger}</span> : null}
                          {r.logger && attrs ? ' · ' : ''}
                          {attrs}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td translate="no">
                    {r.device_model || '–'}
                    {r.os_name ? (
                      <span className="t2">{`${r.os_name} ${r.os_version}`.trim()}</span>
                    ) : null}
                  </td>
                  <td className="last">
                    {r.session_id ? (
                      <Link
                        className="lk"
                        to="/o/$orgId/p/$projectId/sessions/$sessionId"
                        params={{ orgId, projectId, sessionId: r.session_id }}
                        search={carry}
                        translate="no"
                      >
                        {shortId(r.session_id)}
                      </Link>
                    ) : (
                      '–'
                    )}
                  </td>
                </HovRow>
                {expanded ? (
                  <tr className="lg-x">
                    <td colSpan={5}>
                      <Json fields={recordFields(r)} />
                      <div className="lg-xl">
                        {r.session_id ? (
                          <Link
                            className="lk"
                            to="/o/$orgId/p/$projectId/sessions/$sessionId"
                            params={{ orgId, projectId, sessionId: r.session_id }}
                            search={carry}
                            translate="no"
                          >
                            {t('logs.x.session', { id: shortId(r.session_id) })}
                          </Link>
                        ) : null}
                        {r.trace_id ? (
                          <Link
                            className="lk"
                            to="/o/$orgId/p/$projectId/traces/$traceId"
                            params={{ orgId, projectId, traceId: r.trace_id }}
                            search={carry}
                            translate="no"
                          >
                            {t('logs.x.trace', { id: shortId(r.trace_id) })}
                          </Link>
                        ) : null}
                        {screen ? (
                          <Link
                            className="lk"
                            to="/o/$orgId/p/$projectId/performance/screens/detail"
                            params={{ orgId, projectId }}
                            search={{ ...carry, screen }}
                            translate="no"
                          >
                            {t('logs.x.screen', { name: screen })}
                          </Link>
                        ) : null}
                        <Btn
                          size="sm"
                          onClick={async () =>
                            show(
                              (await copyText(
                                r.logger
                                  ? `${r.logger}: ${r.body || r.event_name}`
                                  : r.body || r.event_name,
                              ))
                                ? t('logs.x.copied')
                                : t('issues.copyFailed'),
                            )
                          }
                        >
                          <Icon name="copy" small />
                          {t('logs.x.copy')}
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </Table>
      {toast}
    </>
  )
}

/**
 * The fields as JSON, keys and values styled as the mock's code block. Long values wrap at word
 * and punctuation breaks first, each line hanging under its value, never back at the key's column
 * (Mosaic, APS-379); Copy message copies the record's own text, never the wrapped lines.
 */
function Json({ fields }: { fields: [string, string | number][] }) {
  return (
    <div className="code code-d wrap" translate="no">
      <pre className="lg-json">
        <span className="lg-jb">{'{'}</span>
        {fields.map(([k, v], i) => (
          <span key={k} className="lg-jl">
            <span>
              <span className="k">{JSON.stringify(k)}</span>
              {': '}
            </span>
            <span className="s">
              {typeof v === 'number' ? String(v) : JSON.stringify(v)}
              {i < fields.length - 1 ? ',' : ''}
            </span>
          </span>
        ))}
        <span className="lg-jb">{'}'}</span>
      </pre>
    </div>
  )
}

/** One facet's values as toggles: the selected value checked, its bar against the largest. */
function Facets({
  data,
  search,
  onChange,
}: {
  data: ExploreLogFacets
  search: LogsSearch
  onChange: (patch: Partial<LogsSearch>) => void
}) {
  const { t } = useI18n()
  const i18n = useI18n()
  return (
    <div className="lg-facets">
      {FACETS.map((facet) => {
        const token = TOKEN_KEY[FACET_TOKEN[facet]]
        const current = search[token]
        let values = data[facet]
        if (facet === 'level') {
          // Every level, most severe first; trace reads with debug's filter off.
          const counts = new Map(values.map((v) => [v.value, v.count]))
          values = [...LOG_LEVELS]
            .reverse()
            .map((value) => ({ value, count: counts.get(value) ?? 0 }))
        } else if ((facet === 'logger' || facet === 'screen') && values.length === 0 && !current) {
          // No record names one yet (the SDKs send them from APS-364): no empty facet.
          return null
        }
        const max = Math.max(1, ...values.map((v) => v.count))
        const label = t(`logs.facet.${facet}` as MessageId)
        return (
          <div key={facet}>
            <p className="lg-fk">{label}</p>
            {values.length === 0 ? (
              <p className="note">{t('logs.facet.none')}</p>
            ) : (
              values.map((v) => {
                const on =
                  facet === 'level'
                    ? !!current && rank(v.value) >= rank(current)
                    : current === v.value
                return (
                  <button
                    key={v.value}
                    type="button"
                    className={['lg-fv', on ? 'on' : '', v.count === 0 ? 'zero' : '']
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={on}
                    onClick={() => onChange({ [token]: current === v.value ? undefined : v.value })}
                  >
                    <span translate="no">{v.value}</span>
                    <span className="lg-b" aria-hidden="true">
                      <i style={{ width: `${(v.count / max) * 100}%` }} />
                    </span>
                    <span>{compactCount(i18n, v.count)}</span>
                  </button>
                )
              })
            )}
          </div>
        )
      })}
    </div>
  )
}
